<script setup lang="ts">
import { useMergedReportImages } from "@/composable/useMergedReportImages";
import { computed, ref, watch } from "vue";

import DRBtn from "@/components/UI/DRBtn.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import VisualiseImage from "../steps/findings/VisualiseImage.vue";
import DRDisplayList from "@/components/UI/items/DRDisplayList.vue";
import {
  useCreateReportImage,
  useCreateReportImagesFromDiagnostic,
} from "@/stores/queries/report-images.query";

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    diagnosticId: number;
    reportId: number;
    loading?: boolean;
    title?: string;
    message?: string;
    confirmLabel?: string;
    prioritySectionId?: number; // Section to prioritize and badge
    excludeFindingId?: number; // Exclude images already associated with this finding
    multiple?: boolean; // Allow multiple selection
  }>(),
  {
    multiple: false,
  },
);

const diagnosticId = computed(() => props.diagnosticId);
const reportId = computed(() => props.reportId);

// Use merged images (DiagnosticImages + ReportImages)
const { mergedImages, isLoading: isLoadingImages } = useMergedReportImages(
  diagnosticId,
  reportId,
);

// Create report images on-demand for virtual entries
const { mutateAsync: createReportImage, isPending: isCreatingImage } =
  useCreateReportImage(reportId);

const { isPending: isCreatingBatch } =
  useCreateReportImagesFromDiagnostic(reportId);

// Filter and sort images: exclude already associated, prioritySectionId first
const sortedImages = computed(() => {
  if (!mergedImages.value) return [];

  // Filter out images already associated with the finding
  let images = mergedImages.value;
  if (props.excludeFindingId) {
    images = images.filter(
      (img) => !img.findings?.some((f) => f.id === props.excludeFindingId),
    );
  }

  if (!props.prioritySectionId) return images;

  const inSection = images.filter(
    (img) => img.sortedSectionId === props.prioritySectionId,
  );
  const others = images.filter(
    (img) => img.sortedSectionId !== props.prioritySectionId,
  );
  return [...inSection, ...others];
});

const isInPrioritySection = (diagnosticImageId: number) => {
  if (!props.prioritySectionId || !mergedImages.value) return false;
  const image = mergedImages.value.find(
    (img) => img.diagnosticImageId === diagnosticImageId,
  );
  return image?.sortedSectionId === props.prioritySectionId;
};

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "confirm", reportImageIds: number[]): void;
}>();

const show = computed({
  get: () => props.modelValue,
  set: (value) => {
    emits("update:modelValue", value);
  },
});

// Selection state - stores diagnosticImageIds for selection
const selectedDiagnosticImageIds = ref<Set<number>>(new Set());

const toggleSelection = (diagnosticImageId: number) => {
  if (props.multiple) {
    if (selectedDiagnosticImageIds.value.has(diagnosticImageId)) {
      selectedDiagnosticImageIds.value.delete(diagnosticImageId);
    } else {
      selectedDiagnosticImageIds.value.add(diagnosticImageId);
    }
    // Force reactivity
    selectedDiagnosticImageIds.value = new Set(
      selectedDiagnosticImageIds.value,
    );
  } else {
    // Single selection mode
    if (selectedDiagnosticImageIds.value.has(diagnosticImageId)) {
      selectedDiagnosticImageIds.value.clear();
    } else {
      selectedDiagnosticImageIds.value.clear();
      selectedDiagnosticImageIds.value.add(diagnosticImageId);
    }
    selectedDiagnosticImageIds.value = new Set(
      selectedDiagnosticImageIds.value,
    );
  }
};

const isSelected = (diagnosticImageId: number) =>
  selectedDiagnosticImageIds.value.has(diagnosticImageId);

const hasSelection = computed(() => selectedDiagnosticImageIds.value.size > 0);

const isConfirming = ref(false);

const confirm = async () => {
  if (!hasSelection.value) return;

  isConfirming.value = true;
  try {
    const reportImageIds: number[] = [];

    // For each selected diagnostic image, get or create the report image
    for (const diagnosticImageId of selectedDiagnosticImageIds.value) {
      const mergedImage = mergedImages.value?.find(
        (img) => img.diagnosticImageId === diagnosticImageId,
      );

      if (!mergedImage) continue;

      if (mergedImage.isVirtual) {
        // Create the report image first
        const newReportImage = await createReportImage({
          diagnosticImageId,
          reportId: props.reportId,
        });
        reportImageIds.push(newReportImage.id);
      } else {
        // Use existing report image ID
        reportImageIds.push(mergedImage.reportImageId!);
      }
    }

    emits("confirm", reportImageIds);
    selectedDiagnosticImageIds.value = new Set();
  } finally {
    isConfirming.value = false;
  }
};

// Reset selection when modal closes
watch(show, (visible) => {
  if (!visible) {
    selectedDiagnosticImageIds.value = new Set();
  }
});

const isProcessing = computed(
  () =>
    props.loading ||
    isCreatingImage.value ||
    isCreatingBatch.value ||
    isConfirming.value,
);
</script>

<template>
  <el-dialog
    class="galery-dialog"
    v-model="show"
    fullscreen
    :show-close="false"
    append-to-body
  >
    <template #header> <div></div></template>
    <template #default>
      <div
        class="dialog-layout h-100"
        v-loading="isProcessing || isLoadingImages"
      >
        <div class="dialog-main">
          <div class="dialog-header">
            <div class="d-flex flex-column text-center">
              <div class="h5">{{ title }}</div>
              <div v-if="message">{{ message }}</div>
            </div>
            <div class="header-actions">
              <DRBtnIcon
                variant="flat"
                icon="bi-x-lg"
                :size="40"
                @click="show = false"
              />
            </div>
          </div>

          <div class="dialog-body">
            <div class="col" style="overflow-y: auto">
              <div class="p-3">
                <DRDisplayList
                  :data="sortedImages"
                  key-index="diagnosticImageId"
                  class="mt-2"
                  display="grid"
                  :minWidth="300"
                >
                  <template #item="{ item: image }">
                    <div
                      :class="{
                        selectedimage: isSelected(image.diagnosticImageId),
                        hideimage:
                          !multiple &&
                          hasSelection &&
                          !isSelected(image.diagnosticImageId),
                        'priority-image': isInPrioritySection(
                          image.diagnosticImageId,
                        ),
                      }"
                      @click="toggleSelection(image.diagnosticImageId)"
                      class="gallery-item cursor-pointer position-relative"
                    >
                      <VisualiseImage
                        :id="image.fileId"
                        fill
                        hide-preview
                        style="height: 200px"
                      />
                      <span
                        v-if="isInPrioritySection(image.diagnosticImageId)"
                        class="section-badge"
                      >
                        <el-icon size="12"
                          ><i class="bi-folder-check"></i
                        ></el-icon>
                        {{ $t("report:gallery.sameSection") }}
                      </span>
                      <!-- Selection indicator -->
                      <div
                        v-if="isSelected(image.diagnosticImageId)"
                        class="selection-indicator"
                      >
                        <el-icon size="24"><i class="bi-check-lg"></i></el-icon>
                      </div>
                    </div>
                  </template>
                </DRDisplayList>
              </div>
            </div>
          </div>

          <div class="dialog-footer">
            <DRBtn
              :loading="isProcessing"
              variant="primary"
              size="lg"
              @click="confirm"
              :label="
                multiple && selectedDiagnosticImageIds.size > 1
                  ? `${confirmLabel} (${selectedDiagnosticImageIds.size})`
                  : confirmLabel
              "
              :disabled="!hasSelection"
            />
          </div>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss">
.form-label,
.galery-dialog .el-form-item__label {
  font-weight: 700;
  font-size: 0.9rem;
}
.galery-dialog.el-dialog.is-fullscreen {
  border-radius: 0 !important;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.galery-dialog .dialog-layout {
  display: flex;
  flex: 1;
  height: 100%;
}

.galery-dialog .dialog-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.galery-dialog .dialog-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--el-bg-color, #fff);
  border-bottom: 1px solid var(--el-border-color, #ebeef5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 56px;
}

.galery-dialog .header-actions {
  position: absolute;
  right: 16px;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
}

.galery-dialog .dialog-body {
  flex: 1;
  overflow: auto;
}

.galery-dialog .dialog-footer {
  position: sticky;
  bottom: 0;
  background: var(--el-bg-color, #fff);
  border-top: 1px solid var(--el-border-color, #ebeef5);
  padding: 12px 16px;
  display: flex;
  justify-content: center;
}

.galery-dialog .el-dialog__header {
  padding-bottom: 0 !important;
}

.galery-dialog .el-dialog__body {
  height: 100% !important;
}

.selectedimage {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
  transform: scale(1.05);
}

.hideimage {
  opacity: 0.4 !important;
}

.section-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: var(--bs-primary);
  color: white;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.gallery-item {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
  border: 3px solid transparent;

  &:hover {
    border-color: var(--bs-gray-400);
  }
}

.selection-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--bs-success);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
